// @flow
import type { FSA } from 'types/redux';
import type { ActiveBookingDto } from 'types/bookings';
import type { Location } from 'types/entities';
import type { Simulation } from 'types/dashboard';
import { API_REQUEST } from 'middlewares/api-middleware';
import { MAPBOX_REQUEST } from 'middlewares/mapbox-middleware';
import type { Errors } from '../types/errors';
import type { ResourceGenerateRequest } from '../types/resource';

export const SHOW_PASSENGERS_MODAL: string = 'SHOW_PASSENGERS_MODAL';
export function showPassengersModal(show: boolean): FSA {
  return {
    type: SHOW_PASSENGERS_MODAL,
    payload: show,
  };
}

export const SHOW_PASSENGER_EDIT_MODAL: string = 'SHOW_PASSENGER_EDIT_MODAL';
export function showPassengerEditModal(passengerID: ?string): FSA {
  return {
    type: SHOW_PASSENGER_EDIT_MODAL,
    payload: passengerID,
  };
}

export const SHOW_DRIVERS_MODAL: string = 'SHOW_DRIVERS_MODAL';
export function showDriversModal(show: boolean): FSA {
  return {
    type: SHOW_DRIVERS_MODAL,
    payload: show,
  };
}

export const SHOW_DRIVER_EDIT_MODAL: string = 'SHOW_DRIVER_EDIT_MODAL';
export function showDriverEditModal(driverID: ?string): FSA {
  return {
    type: SHOW_DRIVER_EDIT_MODAL,
    payload: driverID,
  };
}

export const SHOW_DRIVER_LOCATION_MODAL: string = 'SHOW_DRIVER_LOCATION_MODAL';
export function showDriverLocationModal(driverID: ?string): FSA {
  return {
    type: SHOW_DRIVER_LOCATION_MODAL,
    payload: driverID,
  };
}

export const SHOW_DRIVER_FARE_MODAL: string = 'SHOW_DRIVER_FARE_MODAL';
export function showDriverFareModal(driverID: string, bookingID: string): FSA {
  return {
    type: SHOW_DRIVER_FARE_MODAL,
    payload: {
      driverID,
      bookingID,
    },
  };
}

export const CLOSE_DRIVER_FARE_MODAL: string = 'CLOSE_DRIVER_FARE_MODAL';
export function closeDriverFareModal(): FSA {
  return {
    type: CLOSE_DRIVER_FARE_MODAL,
    payload: null,
  };
}

export const DRIVER_DRAG: string = 'DRIVER_DRAG';
export function driverDrag(driverID: ?string): FSA {
  return {
    type: DRIVER_DRAG,
    payload: driverID,
  };
}

export const UNSET_NEWLY_CREATED_BOOKING: string = 'UNSET_NEWLY_CREATED_BOOKING';
export function unsetNewlyCreatedBooking(): FSA {
  return {
    type: UNSET_NEWLY_CREATED_BOOKING,
    payload: null,
  };
}

export const SET_ACTIVE_BOOKING: string = 'SET_ACTIVE_BOOKING';
export function setActiveBooking(booking?: ActiveBookingDto): FSA {
  return {
    type: SET_ACTIVE_BOOKING,
    payload: booking,
  };
}

export const UNSET_ACTIVE_BOOKING: string = 'UNSET_ACTIVE_BOOKING';
export function unsetActiveBooking(): FSA {
  return {
    type: UNSET_ACTIVE_BOOKING,
    payload: null,
  };
}

export const LOGIN: string = 'LOGIN';
export function login(username: string, password: string): FSA {
  return {
    type: LOGIN,
    payload: {
      method: 'POST',
      url: '/tokens/new',
      data: {
        username,
        password,
      },
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const LOGOUT: string = 'LOGOUT';
export function logout(): FSA {
  return {
    type: LOGOUT,
    payload: {
      method: 'DELETE',
      url: '/tokens/delete',
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const VALIDATE_TOKEN: string = 'VALIDATE_TOKEN';
export function validateToken(): FSA {
  return {
    type: VALIDATE_TOKEN,
    payload: {
      method: 'POST',
      url: '/tokens/validate',
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const REVERSE_GEO: string = 'REVERSE_GEO';
export function reverseGeo(location: Location): FSA {
  return {
    type: REVERSE_GEO,
    payload: {
      query: 'reverseGeocode',
      params: {
        query: location,
        limit: 1,
      },
    },
    meta: {
      [MAPBOX_REQUEST]: true,
    },
  };
}

export const FORWARD_GEO: string = 'FORWARD_GEO';
export function forwardGeo(locationQuery: string, city: string, loc?: Location): FSA {
  return {
    type: FORWARD_GEO,
    payload: {
      query: 'forwardGeocode',
      params: {
        query: locationQuery,
        limit: 5,
        countries: [city],
        proximity: loc,
      },
    },
    meta: {
      [MAPBOX_REQUEST]: true,
    },
  };
}

export const START_SIMULATION: string = 'START_SIMULATION';
export function startSimulation(simulation: Simulation): FSA {
  return {
    type: START_SIMULATION,
    payload: {
      method: 'POST',
      url: '/simulations/',
      data: {
        passengers: simulation.numPassengers,
        drivers: simulation.numDrivers,
        cityCode: simulation.city,
        cohortID: simulation.cohortID,
        timeout: simulation.timeout,
        locationDistributor: simulation.locationDistributor,
        taxiTypeDistribution: simulation.taxiTypeDistribution,
      },
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const SIMULATION_GET_REPORT: string = 'SIMULATION_GET_REPORT';
export function simulationReportLoad(successfulRides: number, totalBookings: number,
  successRate: number, allocationRate: number, passengerIDs: Array<string>,
  driverIDs: Array<string>, errors: Errors, timeoutEvent: boolean, bookings: Object): FSA {
  return {
    type: SIMULATION_GET_REPORT,
    payload: {
      successfulRides,
      totalBookings,
      successRate,
      allocationRate,
      passengerIDs,
      driverIDs,
      errors,
      timeoutEvent,
      bookings,
    },
  };
}

export const RESOURCE_CREATION_ERROR: string = 'RESOURCE_CREATION_ERROR';
export function resourceCreationError(): FSA {
  return {
    type: RESOURCE_CREATION_ERROR,
    payload: null,
  };
}

export const SIMULATION_TAXI_TYPES: string = 'SIMULATION_TAXI_TYPES';
export function getTaxiTypes(cityCode: string): FSA {
  return {
    type: SIMULATION_TAXI_TYPES,
    payload: {
      method: 'GET',
      url: '/simulations/available-taxi-types/'.concat(cityCode),
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const GET_TOKEN_LIST: string = 'GET_TOKEN_LIST';
export function getTokenList(): FSA {
  return {
    type: GET_TOKEN_LIST,
    payload: {
      method: 'GET',
      url: '/tokens/',
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const ADD_NEW_TOKEN: string = 'ADD_NEW_TOKEN';
export function addNewToken(): FSA {
  return {
    type: ADD_NEW_TOKEN,
    payload: {
      method: 'PUT',
      url: '/tokens/add',
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const ACTIVATE_TOKEN: string = 'ACTIVATE_TOKEN';
export function activateToken(token: string): FSA {
  return {
    type: ACTIVATE_TOKEN,
    payload: {
      method: 'POST',
      url: '/tokens/activate',
      data: {
        tokenID: token,
      },
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const DEACTIVATE_TOKEN: string = 'DEACTIVATE_TOKEN';
export function deactivateToken(token: string): FSA {
  return {
    type: DEACTIVATE_TOKEN,
    payload: {
      method: 'POST',
      url: '/tokens/deactivate',
      data: {
        tokenID: token,
      },
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const DELETE_TOKEN: string = 'DELETE_TOKEN';
export function deleteToken(token: string): FSA {
  return {
    type: DELETE_TOKEN,
    payload: {
      method: 'DELETE',
      url: '/tokens/remove',
      data: {
        tokenID: token,
      },
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const SIMULATION_DASHBOARD: string = 'SIMULATION_DASHBOARD';
export function getSimulations(status: number, searchLimit: number, exclusiveStartKey: Object): FSA {
  return {
    type: SIMULATION_DASHBOARD,
    payload: {
      method: 'POST',
      url: `/simulations/dashboard?status=${status.toString(10)}`,
      data: {
        limit: searchLimit,
        startKey: exclusiveStartKey,
      },
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const GET_SIMULATION_REPORT: string = 'GET_SIMULATION_REPORT';
export function getSimulationReport(simulationID: string): FSA {
  return {
    type: GET_SIMULATION_REPORT,
    payload: {
      method: 'GET',
      url: `/simulations/report/${simulationID}`,
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const TOGGLE_SIMULATION_STATUS: string = 'TOGGLE_SIMULATION_STATUS';
export function toggleSimulationStatus(status: number, searchLimit: number): FSA {
  return {
    type: TOGGLE_SIMULATION_STATUS,
    payload: {
      method: 'POST',
      url: `/simulations/dashboard?status=${status.toString(10)}`,
      data: {
        limit: searchLimit,
      },
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const RESET_SIMULATION_DASHBOARD: string = 'RESET_SIMULATION_DASHBOARD';
export function resetSimulationDashboard(): FSA {
  return {
    type: RESET_SIMULATION_DASHBOARD,
    payload: null,
  };
}

export const GENERATE_RESOURCES: string = 'GENERATE_RESOURCES';
export function generateResources(resourceGenerateRequest: ResourceGenerateRequest): FSA {
  const isShortLived = (resourceGenerateRequest.shortLived === '0');
  return {
    type: GENERATE_RESOURCES,
    payload: {
      method: 'POST',
      url: '/resources/create',
      data: {
        name: resourceGenerateRequest.name,
        passengers: resourceGenerateRequest.numPassengers,
        drivers: resourceGenerateRequest.numDrivers,
        cityCode: resourceGenerateRequest.cityCode,
        shortLived: isShortLived,
      },
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const RESET_RESULTANT_COHORTS: string = ' RESET_RESULTANT_COHORTS';
export function resetResultantCohorts(): FSA {
  return {
    type: RESET_RESULTANT_COHORTS,
    payload: null,
  };
}

export const CONCEDO_LOGIN: string = 'CONCEDO_LOGIN';
export function loginWithConcedo(authToken: string): FSA {
  return {
    type: CONCEDO_LOGIN,
    payload: {
      method: 'POST',
      url: '/concedo/new',
      data: {
        authToken,
      },
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const CONCEDO_CHECK: string = 'CONCEDO_CHECK';
export function checkConcedoToken(concedoToken: string): FSA {
  return {
    type: CONCEDO_CHECK,
    payload: {
      method: 'POST',
      url: '/concedo/check',
      data: {
        concedoToken,
      },
    },
    meta: {
      [API_REQUEST]: true,
    },
  };
}

export const LOCAL_LOGOUT: string = 'LOCAL_LOGOUT';
export function simpleLogout(): FSA {
  return {
    type: LOCAL_LOGOUT,
    payload: null,
  };
}



/** WEBPACK FOOTER **
 ** ./src/js/actions/app.js
 **/